// public/js/pages/map.js
// Map page controller (MapLibre GL JS)

import {
  initMap,
  renderFilteredMarkers,
  clearAllMapData,
  fitAllMarkers,
  loadAssetHistoryAndDrawRoute,
  showAssetPopup,
  centerOnAsset,
  getMapInstance,
  setBaseStyle,
  getStoredBaseLayerId,
  BASE_LAYERS,
  restoreTrailsAfterStyleChange,
  appendLivePosition,
  isTrailGradientEnabled,
  setTrailGradientEnabled,
  isRecordMode,
  setRecordMode
} from '../modules/map.js';

import { currentFilteredAssets } from '../modules/map.js';
import { CLUSTER_MIN_ASSETS } from '../modules/map/constants.js';

import { mapFilterManager } from '../modules/filters/filter-manager.js';
import { assetTypeFilter } from '../modules/filters/asset-type-filter.js';
import { searchFilter } from '../modules/filters/search-filter.js';
import { clusteringFilter } from '../modules/filters/clustering-filter.js';
import { traveledFilter } from '../modules/filters/traveled-filter.js';
import { beaconFilter } from '../modules/filters/beacon-filter.js';

import { poiState } from '../features/pois/poi-state.js';
import { ensurePoiData } from '../features/pois/poi-data.js';
import { renderPoiMarkers, startPlaceMode, cancelPlaceMode, centerOnPoi, setGeofencesVisible, startPolygonDraw, cancelPolygonDraw } from '../features/pois/poi-map-layer.js';
import { setOverlaysVisible } from '../features/pois/poi-overlay-layer.js';
import { spinnerSVG } from '../modules/ui/spinner.js';
import { isAccuracyEnabled, setAccuracyEnabled } from '../modules/map/accuracy.js';
import { createMapSearchControl } from '../modules/map/search-control.js';
import * as assetStore from '../features/assets/asset-store.js';

let currentMap = null;
let mapControlsAdded = false;
window.currentViewedHistoryAssetId = null;
let cachedMapAssets = null;
let pendingFocusAssetId = null;   // set when arriving at #map to just zoom (no popup)
let _mapPollTimer = null;         // periodic delta poll while the map is open
const MAP_POLL_MS = 45000;

// Zoom/center on an asset WITHOUT opening its info panel or loading history.
// Used by the static map on the asset detail page.
function focusOnAsset(assetUUID) {
  if (!currentMap || !assetUUID) return false;
  const a = currentFilteredAssets.find(x => x.assetUUID === assetUUID);
  const lat = parseFloat(a?.lastLat);
  const lon = parseFloat(a?.lastLon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  currentMap.flyTo({ center: [lon, lat], zoom: Math.max(currentMap.getZoom(), 15), duration: 700 });
  return true;
}

// === View-on-Map support (from asset action menu etc.) ===
function setupViewAssetOnMapListener() {
  const handler = (e) => {
    const { assetUUID } = e.detail || {};
    if (!assetUUID || !currentMap) return;

    const asset = currentFilteredAssets.find(a => a.assetUUID === assetUUID);
    if (asset) {
      // Same as clicking the asset on the map: floating info panel + history.
      showAssetPopup(asset, currentMap);
    }
  };

  window.addEventListener('viewAssetOnMap', handler);
  console.log('[Map] viewAssetOnMap listener registered');
}

export async function initMapPage() {
  // Fire the data fetches FIRST — they're independent of the map, so their
  // (possibly cold-start) server time overlaps map creation + tile loading
  // instead of running after it. sync() dedups in-flight, so the
  // loadAndFilterMapData() call below reuses this same request rather than
  // starting a second one. ensurePoiData is cache-first (same idea).
  const assetPrefetch = assetStore.sync().catch(() => {});
  const poiPrefetch = ensurePoiData().catch(() => {});

  // Clear any stale map data (in case of hot navigation). The route is no
  // longer cleared by marker re-renders, so drop it (and the info panel)
  // explicitly here; frozen record-mode trails intentionally survive.
  try {
    const mapMod = await import('../modules/map.js');
    mapMod.clearAllMapData?.();
    const m = mapMod.getMapInstance?.();
    if (m) {
      mapMod.hideAssetInfoPanel?.();
      mapMod.clearRoute?.(m);
    }
  } catch (e) {}

  document.getElementById('main-content').classList.add('d-none');

  const mapWrapper = document.getElementById('map-wrapper');
  if (mapWrapper) mapWrapper.classList.remove('d-none');

  showMapFiltersDirectlyUnderMapLink();

  if (!currentMap) {
    currentMap = await initMap('map');
  }

  if (currentMap) {
    // Ensure correct size after possible tab/container change
    setTimeout(() => {
      try { currentMap.resize(); } catch (e) {}
    }, 80);

    if (!mapControlsAdded) {
      addMapCustomControls(currentMap);
      mapControlsAdded = true;
    }

    // Data was already prefetched at the top (assetPrefetch / poiPrefetch),
    // concurrently with the map init above. These reuse those in-flight requests.
    await loadAndFilterMapData();
    startMapPoll();
    await poiPrefetch;
    await setupPois();

    // If we navigated here to focus a specific asset (from the asset page map),
    // zoom to it once the data is rendered — without opening the info panel.
    if (pendingFocusAssetId) {
      const id = pendingFocusAssetId;
      pendingFocusAssetId = null;
      setTimeout(() => focusOnAsset(id), 150);
    }
  }

  // Register the view-on-map listener once
  if (!window.__mapViewListenerRegistered) {
    setupViewAssetOnMapListener();
    window.__mapViewListenerRegistered = true;
  }
}

// Render POIs on the map and wire the POI window hooks (place mode + focus).
async function setupPois() {
  if (!window.__poiMapWired) {
    poiState.onChange(() => renderPoiMarkers(currentMap));

    // "+" in the sidebar → place a pin, then open the create modal.
    window.poiStartPlace = () => {
      if (!currentMap) return;
      startPlaceMode(currentMap, ({ lat, lng }) => window.openPoiModal?.({ mode: 'create', lat, lng }),
        { title: 'New POI', hint: 'Tap the map where the POI belongs' });
    };
    // Search result click → fly to the POI with a relatively wide view.
    window.poiFocus = (poiId, zoomedOut = true) => centerOnPoi(currentMap, poiId, zoomedOut);
    // Sidebar toggle: show/hide the geofence circles layer.
    window.poiToggleGeofences = (visible) => setGeofencesVisible(currentMap, visible);
    // Sidebar toggle: show/hide all custom map overlays.
    window.poiToggleOverlays = (visible) => setOverlaysVisible(currentMap, visible);
    // POI modal → draw a geofence polygon on the map (geofencing Phase 2).
    window.poiDrawPolygon = ({ center, onDone, onCancel, initial = null } = {}) => {
      if (!currentMap) { onCancel?.(); return; }
      if (center && Number.isFinite(+center.lat) && Number.isFinite(+center.lng)) {
        currentMap.flyTo({ center: [+center.lng, +center.lat], zoom: Math.max(currentMap.getZoom(), 14), duration: 500 });
      }
      startPolygonDraw(currentMap, { onDone, onCancel, initial });
    };
    // Overlay calibration → pick one geo point on the map (single click).
    window.poiPickMapPoint = (onPicked, onCancel) => {
      if (!currentMap) { onCancel?.(); return; }
      startPlaceMode(currentMap, ({ lat, lng }) => onPicked?.({ lat, lng }),
        { title: 'Pick a point', hint: 'Tap the reference point on the map', onCancel });
    };

    window.__poiMapWired = true;
  }

  // Load through the 24h cache: fresh storage is reused (no backend call),
  // otherwise it fetches once and caches. setData → onChange → renderPoiMarkers.
  await ensurePoiData();
}

// Filter panel — inserted directly under the "Map" nav link
function showMapFiltersDirectlyUnderMapLink() {
  const mapLink = document.querySelector('#nav-menu a[href="#map"]');
  if (!mapLink) return;

  let panel = document.getElementById('map-filter-panel');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'map-filter-panel';
    panel.className = 'px-3 py-2 border-top border-secondary mt-1';
    // Below the POIs sub-item if that exists, so the indented POIs entry stays
    // attached to the Map link it belongs to instead of being pushed away by
    // the filters.
    const poiNav = document.getElementById('poi-nav');
    (poiNav || mapLink).insertAdjacentElement('afterend', panel);
  }

  panel.classList.remove('d-none');
  // POIs are a Map sub-menu — reveal it while the map page is active.
  document.getElementById('poi-nav')?.classList.remove('d-none');
  panel.innerHTML = `
    <div class="small text-white-50 mb-2 ps-3 fw-bold">FILTERS</div>
    <div id="sidebar-filters-container" class="ps-3"></div>
  `;

  const container = document.getElementById('sidebar-filters-container');

  if (mapFilterManager.filters.size === 0) {
    mapFilterManager.registerFilter('search', searchFilter);
    mapFilterManager.registerFilter('assetType', assetTypeFilter);
    mapFilterManager.registerFilter('clustering', clusteringFilter);
    mapFilterManager.registerFilter('traveled', traveledFilter);
    mapFilterManager.registerFilter('positionSource', beaconFilter);
  }

  mapFilterManager.renderAll(container);

  searchFilter.attachListeners(container, mapFilterManager);
  assetTypeFilter.attachListeners(container, mapFilterManager);
  clusteringFilter.attachListeners(container, mapFilterManager);
  beaconFilter.attachListeners(container, mapFilterManager);

  // Re-register onChange every time we visit (same pattern as original)
  mapFilterManager.clearOnChange();
  mapFilterManager.onChange((changedKey) => {
    // clustering & traveled don't change which assets exist, only how the cached
    // set is rendered/filtered — re-filter from cache, no refetch needed.
    loadAndFilterMapData(true, changedKey === 'clustering' || changedKey === 'traveled' || changedKey === 'positionSource');
  });
}

export function hideMapFilters() {
  const panel = document.getElementById('map-filter-panel');
  if (panel) {
    panel.classList.add('d-none');
    const container = document.getElementById('sidebar-filters-container');
    if (container) container.innerHTML = '';
  }
  // Hide the POIs sub-menu when leaving the map page.
  document.getElementById('poi-nav')?.classList.add('d-none');
  // Leave any map mode behind as well. Its bar is hidden along with the map
  // wrapper, but the mode itself would survive the page change and still
  // swallow POI clicks on the way back.
  cancelPlaceMode();
  cancelPolygonDraw();
  // A found address is a lookup, not map data - it should not be waiting on
  // the map when the user comes back tomorrow.
  _mapSearch?.close();
  _mapSearch?.clearMarker();
}

function addMapCustomControls(map) {
  const container = document.createElement('div');
  container.style.cssText = `
    display:flex; gap:8px; margin:12px; z-index:2;
    background:rgba(255,255,255,0.95); padding:4px 6px;
    border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15);
  `;

  const fitBtn = document.createElement('button');
  fitBtn.className = 'btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center map-ctrl-btn';
  fitBtn.innerHTML = `<i class="fa-solid fa-expand"></i>`;
  fitBtn.title = 'Fit all markers to view';
  fitBtn.onclick = () => fitAllMarkers(map);

  const refreshBtn = document.createElement('button');
  refreshBtn.className = 'btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center map-ctrl-btn';
  const refreshIcon = `<i class="fa-solid fa-arrows-rotate"></i>`;
  refreshBtn.innerHTML = refreshIcon;
  refreshBtn.title = 'Refresh map data (keeps current zoom & position)';
  refreshBtn.onclick = async () => {
    if (refreshBtn.dataset.loading === '1') return; // ignore re-clicks while busy
    refreshBtn.dataset.loading = '1';
    refreshBtn.disabled = true;
    refreshBtn.innerHTML = spinnerSVG(28); // replace the button content with the spinner
    try {
      await loadAndFilterMapData(true);
      // Re-pull POIs from the backend only when at least one type is shown;
      // otherwise they refresh lazily the next time the user enables one.
      if (poiState.enabledTypeIds.size > 0) {
        await ensurePoiData({ force: true });
        renderPoiMarkers(currentMap);
      }
    } catch (e) {
      console.warn('[map] refresh failed', e);
    } finally {
      // Always restore the button (also on error).
      refreshBtn.innerHTML = refreshIcon;
      refreshBtn.disabled = false;
      refreshBtn.dataset.loading = '0';
    }
  };

  // Base layer selector — switch base map quickly (all key-free providers)
  const layerSelect = document.createElement('select');
  layerSelect.className = 'form-select form-select-sm shadow-sm map-layer-select';
  layerSelect.style.cssText = 'height:42px;cursor:pointer;';
  layerSelect.title = 'Base layer';
  const activeId = getStoredBaseLayerId() || (BASE_LAYERS[0] && BASE_LAYERS[0].id);
  BASE_LAYERS.forEach(l => {
    const opt = document.createElement('option');
    opt.value = l.id;
    opt.textContent = l.label;
    if (l.provider) opt.title = l.provider;
    if (l.id === activeId) opt.selected = true;
    layerSelect.appendChild(opt);
  });
  layerSelect.onchange = () => {
    // setStyle wipes our sources/layers/images; rebuild assets, POIs and the
    // currently viewed route/accuracy once the new style is ready (keep viewport).
    // The route (and any frozen record-mode trails) redraw from the points
    // already in memory — no refetch, extra-loaded history windows survive.
    setBaseStyle(map, layerSelect.value, async () => {
      await loadAndFilterMapData(true, true);
      await renderPoiMarkers(map);
      restoreTrailsAfterStyleChange(map);
    });
  };

  // Toggle buttons (same style as the others) with a corner status dot:
  // green = on, red = off; the button is also highlighted when on.
  const makeToggle = (iconClass, title, isOn, onToggle) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center position-relative map-ctrl-btn';
    b.title = title;
    b.innerHTML = `<i class="${iconClass}"></i>` +
      `<span class="map-toggle-dot" style="position:absolute;top:3px;right:3px;width:9px;height:9px;border-radius:50%;border:1.5px solid #fff;box-shadow:0 0 2px rgba(0,0,0,0.4);"></span>`;
    let on = isOn;
    const paint = () => {
      b.querySelector('.map-toggle-dot').style.background = on ? '#22c55e' : '#ef4444';
      b.style.boxShadow = on ? 'inset 0 0 0 2px #22c55e' : '';
    };
    paint();
    b.onclick = () => { on = !on; paint(); onToggle(on); };
    return b;
  };

  const traveledBtn = makeToggle(
    'fa-solid fa-plane-departure', 'Show only assets that travelled ≥1km in the last 24h',
    (mapFilterManager.getState().traveled || { enabled: false }).enabled,
    (on) => mapFilterManager.updateFilter('traveled', { enabled: on })
  );

  const clusterBtn = makeToggle(
    'fa-solid fa-layer-group', 'Clustering on/off',
    (mapFilterManager.getState().clustering || { enabled: true }).enabled,
    (on) => mapFilterManager.updateFilter('clustering', { enabled: on })
  );
  _clusterBtn = clusterBtn;
  updateClusterBtnAvailability(currentFilteredAssets.length);

  const accuracyBtn = makeToggle(
    'fa-solid fa-circle-dot', 'Position accuracy on/off',
    isAccuracyEnabled(),
    (on) => setAccuracyEnabled(map, on)
  );

  const gradientBtn = makeToggle(
    'fa-solid fa-temperature-half', 'Trail age gradient: blue = old, red = recent',
    isTrailGradientEnabled(),
    (on) => setTrailGradientEnabled(map, on)
  );

  // Record mode: keep every opened asset's trail on the map (frozen in its own
  // colour) instead of clearing it when another asset is opened. Rendered as a
  // classic record button — red pulsing dot while armed.
  const recordBtn = document.createElement('button');
  recordBtn.type = 'button';
  recordBtn.className = 'btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center position-relative map-ctrl-btn';
  recordBtn.title = 'Record trails: keep each opened asset\'s path on the map';
  recordBtn.innerHTML = `<i class="fa-solid fa-record-vinyl"></i>` +
    `<span class="map-toggle-dot" style="position:absolute;top:3px;right:3px;width:9px;height:9px;border-radius:50%;border:1.5px solid #fff;box-shadow:0 0 2px rgba(0,0,0,0.4);"></span>`;
  const paintRecord = () => {
    const on = isRecordMode();
    recordBtn.classList.toggle('map-record-active', on);
    recordBtn.querySelector('.map-toggle-dot').style.background = on ? '#ef4444' : '#9ca3af';
    recordBtn.style.boxShadow = on ? 'inset 0 0 0 2px #ef4444' : '';
  };
  paintRecord();
  recordBtn.onclick = () => { setRecordMode(map, !isRecordMode()); paintRecord(); };

  // On mobile the whole bar collapses behind a single arrow button to free up
  // map space; on desktop the controls are always shown (CSS handles the hide).
  const isMobile = () => window.innerWidth < 768;

  // Top row holds the base-layer selector + the collapse arrow. On mobile the
  // selector is hidden while collapsed and unfolds to the LEFT of the arrow when
  // expanded (the icons unfold downward), so collapsed = just the arrow.
  const topRow = document.createElement('div');
  topRow.className = 'map-controls-row';

  const collapseToggle = document.createElement('button');
  collapseToggle.type = 'button';
  // No Bootstrap d-flex here — its display:flex !important would defeat the
  // desktop "display:none" rule; .map-controls-toggle handles centering itself.
  collapseToggle.className = 'btn btn-sm btn-light shadow-sm map-controls-toggle map-ctrl-btn';
  collapseToggle.title = 'Show/hide map controls';
  collapseToggle.innerHTML = `<i class="fa-solid fa-chevron-down"></i>`;
  // Address search goes FIRST, left of the base-layer selector: it is what a
  // visitor reaches for, and on a phone it stays reachable while the rest of
  // the bar is folded away. It deliberately does not join controlsWrap, which
  // collapses. Without a map key there is no geocoder, so no button either.
  const search = createMapSearchControl(map);
  if (search.available) topRow.append(search.button);
  topRow.append(layerSelect, collapseToggle);

  // The collapsible part: just the small icon buttons (narrow column on mobile).
  // NB: layout lives in CSS (.map-controls-wrap) not inline, so the mobile
  // media query can hide/restack it (inline styles would override the query).
  const controlsWrap = document.createElement('div');
  controlsWrap.className = 'map-controls-wrap';
  controlsWrap.append(traveledBtn, clusterBtn, accuracyBtn, gradientBtn, recordBtn, fitBtn, refreshBtn);

  const setExpanded = (exp) => {
    container.classList.toggle('expanded', exp);
    const icon = collapseToggle.querySelector('i');
    if (icon) icon.className = exp ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down';
  };
  collapseToggle.onclick = () => setExpanded(!container.classList.contains('expanded'));

  // Once the user actuates any icon (or picks a base layer) on mobile, fold up.
  controlsWrap.addEventListener('click', (e) => {
    if (e.target !== controlsWrap && isMobile()) setExpanded(false);
  });
  layerSelect.addEventListener('change', () => { if (isMobile()) setExpanded(false); });

  container.classList.add('map-controls');
  container.append(topRow, search.row, controlsWrap);
  _mapSearch = search;

  // Position top-right (we add it as a custom control via DOM for simplicity)
  const mapContainer = map.getContainer();
  mapContainer.style.position = 'relative';
  container.style.position = 'absolute';
  container.style.top = '0';
  container.style.right = '60px'; // leave space for native controls
  mapContainer.appendChild(container);

  console.log('✅ Map custom controls added');
}

// The clustering button, kept so its availability can follow the asset count.
let _clusterBtn = null;
// The search control, kept so leaving the page can close it and drop its pin.
let _mapSearch = null;

// Clustering only engages from CLUSTER_MIN_ASSETS markers up, so below that the
// button is disabled and says why. It used to flip to green and do nothing,
// which reads exactly like a broken button.
function updateClusterBtnAvailability(count) {
  if (!_clusterBtn) return;
  const usable = count >= CLUSTER_MIN_ASSETS;
  _clusterBtn.disabled = !usable;
  _clusterBtn.style.opacity = usable ? '' : '0.45';
  _clusterBtn.style.cursor = usable ? '' : 'not-allowed';
  _clusterBtn.title = usable
    ? 'Clustering on/off'
    : `Clustering starts at ${CLUSTER_MIN_ASSETS} assets \u2014 ${count} on the map`;
}

async function loadAndFilterMapData(preserveViewport = false, skipFetch = false) {
  try {
    // Filter-only re-render (clustering/traveled toggles) — no data change.
    if (skipFetch && cachedMapAssets) {
      const filtered = mapFilterManager.applyFilters(cachedMapAssets);
      await renderFilteredMarkers(filtered, currentMap, preserveViewport);
      return;
    }

    // Delta cache: paint instantly from the persisted cache, then sync only the
    // changes from the backend (docs/performance.md).
    await assetStore.init();
    let painted = false;
    if (!cachedMapAssets) {
      const cached = assetStore.getCached();
      if (cached.length) {
        cachedMapAssets = cached;
        await renderFilteredMarkers(mapFilterManager.applyFilters(cachedMapAssets), currentMap, preserveViewport);
        painted = true;
      }
    }

    const { assets } = await assetStore.sync();
    cachedMapAssets = assets;
    const filtered = mapFilterManager.applyFilters(cachedMapAssets);
    console.log(`[Map] ${filtered.length} of ${cachedMapAssets.length} assets after filters`);
    updateClusterBtnAvailability(filtered.length);
    // If we already painted from cache, keep the viewport steady after the sync.
    await renderFilteredMarkers(filtered, currentMap, painted ? true : preserveViewport);
  } catch (e) {
    console.error('[Map] loadAndFilterMapData error', e);
    window.showToast?.('Error loading map data', 'danger');
  }
}

// Periodic background delta while the map is open — only re-renders when the
// sync actually returned changes, so a quiet fleet costs one tiny request.
function startMapPoll() {
  stopMapPoll();
  _mapPollTimer = setInterval(async () => {
    if (window.location.hash !== '#map' || !currentMap) return;
    try {
      const { assets, changed, deleted } = await assetStore.sync();
      if (changed.length || deleted.length) {
        cachedMapAssets = assets;
        await renderFilteredMarkers(mapFilterManager.applyFilters(cachedMapAssets), currentMap, true);
        // Live-extend the open asset's trail so the path keeps up with its marker.
        const viewedId = window.currentViewedHistoryAssetId;
        if (viewedId) {
          const viewed = assets.find(a => a.assetUUID === viewedId);
          if (viewed) appendLivePosition(currentMap, viewed);
        }
        console.log(`[Map] poll applied ${changed.length} changed / ${deleted.length} removed`);
      }
    } catch (e) { console.warn('[Map] poll failed', e?.message); }
  }, MAP_POLL_MS);
}

function stopMapPoll() {
  if (_mapPollTimer) { clearInterval(_mapPollTimer); _mapPollTimer = null; }
}

// Stop polling as soon as we navigate away from the map.
window.addEventListener('hashchange', () => { if (window.location.hash !== '#map') stopMapPoll(); });

// Expose helpers on window for debugging / cross-feature wiring
window.refreshMap = () => loadAndFilterMapData(true);
window.centerMapOnAsset = (uuid) => centerOnAsset(uuid, currentMap);

// Navigate to the map and zoom to an asset (no info panel / no history load).
window.focusMapOnAsset = (uuid) => {
  if (!uuid) return;
  if (window.location.hash !== '#map') {
    pendingFocusAssetId = uuid;
    window.location.hash = '#map';
  } else {
    focusOnAsset(uuid);
  }
};

// "View on Map" from the asset action menu: switch to the map and focus the asset.
window.viewAssetOnMap = (assetUUID) => {
  if (!assetUUID) return;
  if (window.location.hash !== '#map') window.location.hash = '#map';
  // Wait for the map page/init to be ready, then dispatch the focus event.
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('viewAssetOnMap', { detail: { assetUUID } }));
  }, currentMap ? 50 : 600);
};
